import { createContext, useEffect, useState, useCallback } from "react";
import { Loader } from "@vds/loaders";
import { Modal, ModalBody, ModalFooter, ModalTitle } from "@vds/modals";
import { Body } from "@vds/typography";
import { Button } from "@vds/buttons";
import { setPrivilegesHelper } from "../utility/privileges";
import { getPrivileges } from "../services/userService/getPrivilege";
import { useOktaAuth } from "@okta/okta-react";
import { getCSPDetails } from "../C4S Decommission - Replacement/services/C4S Decommission - Replacement/getCSPDetails";
import { Privileges } from "../constants/PrivilegeConstants";

export const PrivilegeContext = createContext();
export const PrivilegeContextProvider = (props) => {
  const [privileges, setPrivileges] = useState(new Set());
  const [isWTA, setIsWTA] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [privilegesFetched, setPrivilegesFetched] = useState(false);
  const [privilegesFetchedC360, setPrivilegesFetchedC360] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alertModal, setAlertModal] = useState(false);
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [cspInfo, setCspInfo] = useState(null);

  const extractQueryParams = (queryString) => {
    const params = new URLSearchParams(queryString);
    return {
      accountId: params.get("AccountID"),
      cspId: params.get("ID"),
      userId: params.get("userId"),
    };
  };

  const sortProductsByBundleCoreFirst = (products = []) => {
    if (!Array.isArray(products)) return [];

    const groupedByBundle = products.reduce((acc, product) => {
      const bundleId = product?.BundleId ?? "__NO_BUNDLE__";
      if (!acc[bundleId]) acc[bundleId] = [];
      acc[bundleId].push(product);
      return acc;
    }, {});

    return Object.values(groupedByBundle).flatMap((bundleProducts) => {
      return [...bundleProducts].sort((a, b) => {
        const rankA = a?.featureType === "CORE" ? 0 : 1;
        const rankB = b?.featureType === "CORE" ? 0 : 1;
        // stable sort keeps original API order for items with same rank
        return rankA - rankB;
      });
    });
  };

  const checkIsAuthenticated = useCallback(async () => {
    setAlertModal(false);
    setLoading(true);
    try {
      let response = await getPrivileges();
      if (response.status === 200 && response?.data) {
        setRole(response?.data?.data?.roles ?? "");
        const userLocation = response?.data?.data?.location ?? "";
        setLocation(userLocation);

        // Store location in sessionStorage for axios interceptor
        if (userLocation) {
          sessionStorage.setItem("userLocation", userLocation);
        }

        let updatedPrivileges = new Set(setPrivilegesHelper(response?.data?.data?.privileges ?? []));
        let routeWithParams = oktaAuth.getOriginalUri() ?? window.location.hash;
        // let routeWithParams = window.location.hash;
        let param = routeWithParams.substring(routeWithParams.indexOf("?", 1), routeWithParams.length);
        if (param) {
          const { accountId, cspId, userId } = extractQueryParams(param);
          if (accountId && cspId && userId) {
            try {
              const cspDetails = await getCSPDetails(accountId, cspId, userId);
              if (
                cspDetails.data.data &&
                cspDetails.data.data.CSP.length &&
                cspDetails.data.data.CSP[0].isWTA &&
                updatedPrivileges.has(Privileges.WTA_ENABLED)
              ) {
                updatedPrivileges.add(Privileges.ENABLE_WORKTICKET_PORTAL_MENU_WTA);
                updatedPrivileges.delete(Privileges.ENABLE_WORKTICKET_PORTAL_MENU);
                setIsWTA(true);
                sessionStorage.setItem("isWTA", true);
              } else {
                sessionStorage.setItem("isWTA", false);
              }
              //setCspInfo(cspDetails);
              const sortedProducts = sortProductsByBundleCoreFirst(cspDetails?.data?.data?.Product);

              const normalizedCspDetails = cspDetails?.data?.data
                ? {
                  ...cspDetails,
                  data: {
                    ...cspDetails.data,
                    data: {
                      ...cspDetails.data.data,
                      Product: sortedProducts,
                    },
                  },
                }
                : cspDetails;
              setCspInfo(normalizedCspDetails);
              setPrivilegesFetchedC360(true);
            } catch (error) {
              // Proceed without setting CSP details
            }
          }
        }
        setPrivileges(updatedPrivileges);
        setPrivilegesFetched(true);
      } else {
        setAlertModal(true);
      }
    } catch (error) {
      setAlertModal(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const getOktaCredentials = useCallback(async () => {
    if (!authState || !authState.isAuthenticated) {
      // When user isn't authenticated, forget any user info
      setUserInfo(null);
    } else {
      await oktaAuth
        .getUser()
        .then((info) => {
          setUserInfo(info);
        })
        .catch((err) => {
          setErrorMsg("Something went wrong while fetching user data" + err);
        });
    }
  }, [authState, oktaAuth]);

  useEffect(() => {
    const initializePrivileges = async () => {
      setLoading(true);
      await checkIsAuthenticated();
      await getOktaCredentials();
      setLoading(false);
    };
    const handleHashChange = () => {
      initializePrivileges();
    };
    window.addEventListener("hashchange", handleHashChange);
    initializePrivileges();
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [checkIsAuthenticated, authState, oktaAuth]);
  return (
    <>
      <Loader active={loading} />
      <Modal opened={alertModal} disableOutsideClick closeButton={null}>
        <ModalTitle>Error</ModalTitle>
        <ModalBody>
          <Body size="large" bold>
            We are currently experiencing difficulty in processing your request. Please try again sometime.
          </Body>
        </ModalBody>
        <ModalFooter>
          <Button onClick={checkIsAuthenticated}>Retry</Button>
        </ModalFooter>
      </Modal>
      <PrivilegeContext.Provider
        value={{
          privileges: privileges,
          role: role,
          location: location,
          userInfo: userInfo,
          cspInfo: cspInfo,
          isWTA: isWTA,
          isFirstLoad: isFirstLoad,
          setIsFirstLoad: setIsFirstLoad,
          privilegesFetchedC360: privilegesFetchedC360,
          setAlertModal: setAlertModal,
        }}
      >
        {privilegesFetched && props.children}
      </PrivilegeContext.Provider>
    </>
  );
};
